import type { CapabilitiesSnapshot, MeResponse, PermissionCode } from '../../entities/me/me-contract.types';
import type { StaffNavCatalogEntry } from '../../widgets/navigation/staff-route-catalog';
import type { StaffSPARoutePath } from './staff-spa-route-paths';
import { isStaffPlatformMode } from './staff-platform-mode';

export function getPermissionSet(capabilities: CapabilitiesSnapshot | null | undefined): Set<string> {
  if (!capabilities) return new Set();
  const list = capabilities.permissions_effective?.length
    ? capabilities.permissions_effective
    : (capabilities.permissions ?? []);
  return new Set(list);
}

export function hasPermission(
  capabilities: CapabilitiesSnapshot | null | undefined,
  permission: PermissionCode
): boolean {
  return getPermissionSet(capabilities).has(permission);
}

export function isModuleEnabled(
  capabilities: CapabilitiesSnapshot | null | undefined,
  moduleCode: string
): boolean {
  if (!capabilities?.modules_effective) return false;
  return capabilities.modules_effective[moduleCode] === true;
}

export function isRouteAllowed(
  capabilities: CapabilitiesSnapshot | null | undefined,
  backendRouteKey: StaffSPARoutePath
): boolean {
  if (!capabilities?.routes_allowed) return false;
  return capabilities.routes_allowed.includes(backendRouteKey);
}

function normalizeDenyEntry(raw: unknown): { type?: string; module?: string } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const o = raw as Record<string, unknown>;
  const type = typeof o.type === 'string' ? o.type : undefined;
  const module =
    typeof o.module === 'string' ? o.module : typeof o.module_code === 'string' ? o.module_code : undefined;
  return { type, module };
}

export function getDenyReason(
  capabilities: CapabilitiesSnapshot | null | undefined,
  backendRouteKey: StaffSPARoutePath
): { type?: string; module?: string; raw: unknown } | null {
  const dr = capabilities?.deny_reasons;
  if (!dr || typeof dr !== 'object') return null;
  const raw = (dr as Record<string, unknown>)[backendRouteKey];
  if (raw === undefined) return null;
  const n = normalizeDenyEntry(raw);
  return { ...n, raw };
}

export function canShowNavItem(
  capabilities: CapabilitiesSnapshot | null | undefined,
  item: StaffNavCatalogEntry
): boolean {
  if (!isRouteAllowed(capabilities, item.backendRouteKey)) return false;
  if (item.permissionCode && !hasPermission(capabilities, item.permissionCode)) return false;
  if (item.moduleCode && !isModuleEnabled(capabilities, item.moduleCode)) return false;
  return true;
}

/**
 * Список и карточка организации на платформе (`/superadmin/organizations`, `/superadmin/organizations/:id` → ключ `/platform-organizations` через мост).
 * Гейт: маршрут в `routes_allowed` и `platform:organizations:view` в effective (без `me.role`, без `staff_admin_shell`).
 */
export function canAccessPlatformOrganizationsList(capabilities: CapabilitiesSnapshot | null | undefined): boolean {
  if (!isRouteAllowed(capabilities, '/platform-organizations')) return false;
  return hasPermission(capabilities, 'platform:organizations:view');
}

/** Сводка платформы (`/superadmin/dashboard`). */
export function canAccessPlatformDashboard(capabilities: CapabilitiesSnapshot | null | undefined): boolean {
  if (!isRouteAllowed(capabilities, '/superadmin/dashboard')) return false;
  return hasPermission(capabilities, 'platform:dashboard:view');
}

/** Каталог тарифов (`/superadmin/plans` → ключ `/platform-plans`). */
export function canAccessPlatformPlansList(capabilities: CapabilitiesSnapshot | null | undefined): boolean {
  if (!isRouteAllowed(capabilities, '/platform-plans')) return false;
  return hasPermission(capabilities, 'platform:plans:view');
}

/**
 * Таб «Персонал платформы» (`/superadmin/users/platform`, legacy `/superadmin/platform-users`).
 * Гейт: маршрут в `routes_allowed` и `platform:users:view` в effective.
 */
export function canAccessSuperadminUsersPlatformTab(
  capabilities: CapabilitiesSnapshot | null | undefined
): boolean {
  const routeOk =
    isRouteAllowed(capabilities, '/superadmin/users/platform') ||
    isRouteAllowed(capabilities, '/superadmin/platform-users');
  if (!routeOk) return false;
  return hasPermission(capabilities, 'platform:users:view');
}

export function canAccessPlatformUsersPage(capabilities: CapabilitiesSnapshot | null | undefined): boolean {
  return canAccessSuperadminUsersPlatformTab(capabilities);
}

/**
 * Таб «Клиенты организаций» (`/superadmin/users/clients`, legacy `/superadmin/clients-catalog`).
 * В capabilities маршрут выдаётся только для полномочий суперадмина платформы; в UI гейт по `platform:organizations:view`.
 */
export function canAccessSuperadminUsersClientsTab(
  capabilities: CapabilitiesSnapshot | null | undefined
): boolean {
  const routeOk =
    isRouteAllowed(capabilities, '/superadmin/users/clients') ||
    isRouteAllowed(capabilities, '/superadmin/clients-catalog');
  if (!routeOk) return false;
  return hasPermission(capabilities, 'platform:organizations:view');
}

/**
 * Таб «Роли и права» платформы (`/superadmin/users/roles`): только JWT СА без контекста организации и с `platform:superadmin`.
 */
export function canAccessSuperadminUsersRolesTab(
  capabilities: CapabilitiesSnapshot | null | undefined,
  me: Pick<MeResponse, 'role' | 'organization_id'> | null | undefined,
  impersonateOrganizationId: number | null | undefined
): boolean {
  if (!isRouteAllowed(capabilities, '/superadmin/users/roles')) return false;
  if (!hasPermission(capabilities, 'platform:superadmin')) return false;
  return isStaffPlatformMode(me, impersonateOrganizationId);
}

export function canAccessSuperadminClientsCatalogPage(
  capabilities: CapabilitiesSnapshot | null | undefined
): boolean {
  return canAccessSuperadminUsersClientsTab(capabilities);
}

/**
 * Паритет backend `access.EffectiveSnapshotMatchesBranchAdminRouteShape` — эффективный снимок «как у АФ» (не источник RBAC полностью).
 */
export function effectiveSnapshotMatchesBranchAdminRouteShape(
  capabilities: CapabilitiesSnapshot | null | undefined
): boolean {
  if (!hasPermission(capabilities, 'yclients:read')) return false;
  if (hasPermission(capabilities, 'audit:org_read')) return false;
  return true;
}

/**
 * Пункт сайдбара «Пользователи»: ключ `/superadmin/users` или переходный доступ по legacy-маршрутам табов.
 */
export function canShowSuperadminUsersHubNavItem(
  capabilities: CapabilitiesSnapshot | null | undefined,
  me?: Pick<MeResponse, 'role' | 'organization_id'> | null,
  impersonateOrganizationId?: number | null
): boolean {
  if (isRouteAllowed(capabilities, '/superadmin/users')) return true;
  return (
    canAccessSuperadminUsersPlatformTab(capabilities) ||
    canAccessSuperadminUsersClientsTab(capabilities) ||
    canAccessSuperadminUsersRolesTab(capabilities, me ?? null, impersonateOrganizationId ?? null)
  );
}

/**
 * Платформенный JWT (`users.role` = superadmin или platform_staff): не RBAC-гейт экранов, только узкие UX/HTTP-семейства.
 * platform_staff — обрезанный суперадмин, работает только с платформенными экранами без контекста организации.
 * См. `staff-spa-core-rules.mdc` (исключение «семейство сессии»).
 */
export function isJwtPlatformSuperadminSession(me: Pick<MeResponse, 'role'> | null | undefined): boolean {
  const role = (me?.role ?? '').trim().toLowerCase();
  return role === 'superadmin' || role === 'platform_staff';
}

/**
 * Чтение снимка организации через superadmin list API только для подписи в UI, когда в `/auth/me` нет имени орг.
 * При имперсонации СА в `capabilities.routes_allowed` часто **нет** `/platform-organizations`, хотя JWT остаётся
 * суперадминским — иначе бейдж контекста застревает на нейтральном «Организация». Навигационный гейт не подменяем.
 */
export function canFetchSuperadminOrganizationSnapshotForDisplay(me: MeResponse | null | undefined): boolean {
  if (!me) return false;
  if (isJwtPlatformSuperadminSession(me)) return true;
  return canAccessPlatformOrganizationsList(me.capabilities);
}
